import {AbstractControl, ValidationErrors} from '@angular/forms';

export const PRODUCT_CARD_TEXT_FIELDS = [
  {key: 'details', title: 'Details button', group: 'buttons'},
  {key: 'testDrive', title: 'Test-drive button · automotive cards', group: 'buttons'},
  {key: 'addToCart', title: 'Add-to-cart button · when enabled', group: 'buttons'},
  {key: 'favoriteTitle', title: 'Save to favourites', group: 'other'},
  {key: 'previousPricePrefix', title: 'Previous price prefix', group: 'other'},
  {key: 'priceOnRequest', title: 'Price on request', group: 'other'},
  {key: 'variants', title: 'Variants', group: 'other'},
  {key: 'variantsInStock', title: 'Variants in stock', group: 'other'},
  {key: 'inStockIn', title: 'In stock in', group: 'other'},
  {key: 'productImageAlt', title: 'Product image description', group: 'other'},
  {key: 'imageUnavailable', title: 'Image unavailable', group: 'other'}
] as const;
export type ProductCardTextKey = typeof PRODUCT_CARD_TEXT_FIELDS[number]['key'];
export type ProductCardTexts = Partial<Record<ProductCardTextKey, string>>;
export type ProductCardTextOverrides = Record<string, ProductCardTexts>;
const keys = new Set<string>(PRODUCT_CARD_TEXT_FIELDS.map(field => field.key));

export function productCardTextLanguage(locale: string): string {
  const language = locale.trim().replace(/_/g, '-').toLowerCase().split('-')[0];
  return language === 'nb' ? 'no' : language;
}

export function parseProductCardTextOverrides(value: unknown): ProductCardTextOverrides | null {
  if (!isRecord(value) || Object.keys(value).length > 64) return null;
  const result: ProductCardTextOverrides = {};
  for (const [language, texts] of Object.entries(value)) {
    if (!/^[a-z]{2,3}$/.test(language) || language === 'nb' || !isRecord(texts)) return null;
    const normalized: ProductCardTexts = {};
    for (const [key, text] of Object.entries(texts)) {
      if (!keys.has(key) || typeof text !== 'string' || text.length > 40 ||
        Array.from(text).some(c => c.charCodeAt(0) <= 31 || (c.charCodeAt(0) >= 127 && c.charCodeAt(0) <= 159))) return null;
      if (text.trim()) normalized[key as ProductCardTextKey] = text.trim();
    }
    result[language] = normalized;
  }
  return result;
}

export function validateProductCardTextOverrides(control: AbstractControl): ValidationErrors | null {
  return parseProductCardTextOverrides(control.value) === null ? {productCardTexts: true} : null;
}

interface LegacyCardTexts {
  cardLocale?: string;
  productCardDetailsLabel?: string;
  productCardTestDriveLabel?: string;
  productCardTextOverrides?: ProductCardTextOverrides;
}

/** Convert existing button settings once, before the editor starts managing language maps. */
export function loadProductCardTextOverrides(options?: LegacyCardTexts, localization?: {
  defaultLocale?: string;
  variants?: Record<string, {frontEndOptions?: LegacyCardTexts}>;
}): ProductCardTextOverrides {
  if (options?.productCardTextOverrides != null) return parseProductCardTextOverrides(options.productCardTextOverrides) ?? {};
  const result: ProductCardTextOverrides = {};
  const variantValues = new Map<string, string | null>();
  const addLegacy = (source: LegacyCardTexts | undefined, locale: string, isVariant = false): void => {
    const texts: ProductCardTexts = {};
    if (source?.productCardDetailsLabel?.trim()) texts.details = source.productCardDetailsLabel.trim();
    if (source?.productCardTestDriveLabel?.trim()) texts.testDrive = source.productCardTestDriveLabel.trim();
    const language = productCardTextLanguage(locale);
    for (const [key, text] of Object.entries(texts) as [ProductCardTextKey, string][]) {
      result[language] ??= {};
      if (isVariant) {
        const id = `${language}.${key}`;
        const previous = variantValues.get(id);
        if (previous !== undefined && previous !== text) {
          // Regional conflicts must not choose an arbitrary customer override for the shared language.
          variantValues.set(id, null);
          delete result[language][key];
          continue;
        }
        variantValues.set(id, text);
      }
      result[language][key] = text;
    }
  };
  addLegacy(options, options?.cardLocale || localization?.defaultLocale || 'en-US');
  for (const [locale, variant] of Object.entries(localization?.variants ?? {})) {
    addLegacy(variant.frontEndOptions, locale, true);
  }
  return parseProductCardTextOverrides(result) ?? {};
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
